import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';

import { SamplingCadence, Site } from 'src/shared/types';
import {
    ProjectsQueryResult,
    projectsQuery,
    ProjectQueryResult,
    projectQuery,
    ProjectFiltersQueryResult,
    projectFiltersQuery,
    customerProjectsInBCQuery,
    CustomerProjectsInBCQueryResult,
    enablePdfDownloadQuery,
    getProjectSamplingCadenceQuery,
    projectSitesQuery,
    projectDefinitionQuery,
    ProjectDefinitionQueryResult,
    projectForAdminQuery,
    ProjectsForAdminQueryResult,
    ProjectForAdmin,
    CustomerProjects,
    deleteProjectDefinitionQuery,
    projectListQuery,
    ProjectListResponse,
    ProjectListData,
    ProjectListInput,
    userManagementProjectListQuery,
    UserManagementProjectListResponse,
    UserManagementProjectListInput,
    userCountSummaryQuery,
    UserCountSummaryQueryResult,
    UserCountSummaryQueryParams,
    hasActiveEdnaDataReuploadRequestQuery,
    HasActiveEdnaDataReuploadRequestResult,
    EdnaDataReuploadRequestStatus,
    EdnaDataReuploadRequestData,
    getLastEdnaDataReuploadQuery,
    GetLastEdnaDataReuploadResult,
    requestEdnaDataReuploadReviewMutation,
    RequestEdnaDataReuploadReviewResult,
    approveEdnaDataReuploadMutation,
    ApproveEdnaDataReuploadResult,
    dismissEdnaDataReuploadMutation,
    DismissEdnaDataReuploadResult,
} from './queries/projectsListQuery';

export type {
    EdnaDataReuploadRequestStatus,
    EdnaDataReuploadRequestData,
    EdnaDataReuploadReviewItem,
    EdnaDataReuploadReviewItemDateRange,
} from './queries/projectsListQuery';

type ProjectsQueryParams = {
    customerId: string;
};

type ProjectQueryParams = {
    projectId: string;
    samples?: {
        fromDate: string;
        toDate: string;
        assay: string;
    };
};

type ProjectFiltersQueryParams = {
    projectId: string;
};

type CustomerProjectsQueryParams = {
    input: {
        customerId: string;
        searchText: string;
        limit?: number;
    };
};

interface EnablePdfDownloadResponse {
    admin?: {
        enablePdfDownload?: boolean;
    };
}

interface DeleteProjectDefinitionResponse {
    deleteProjectDefinition: boolean;
}

const projectListGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        projects: builder.query<CustomerProjects, ProjectsQueryParams>({
            query: params => ({
                body: projectsQuery,
                variables: params,
            }),
            transformResponse: (response: ProjectsQueryResult) => {
                return response.customerWithProjects.projects.map(project => ({
                    ...project,
                    projectCode: !project.projectCode || project.projectCode === 'Not set' ? 'Missing' : project.projectCode,
                }));
            },
        }),

        projectsForAdmin: builder.query<ProjectForAdmin[], ProjectsQueryParams>({
            query: params => ({
                body: projectForAdminQuery,
                variables: params,
            }),
            transformResponse: (response: ProjectsForAdminQueryResult) => {
                return response.customer.projects.map(project => ({
                    ...project,
                    projectCode: !project.projectCode || project.projectCode === 'Not set' ? 'Missing' : project.projectCode,
                }));
            },
        }),
        customerProjectsInBC: builder.query<CustomerProjectsInBCQueryResult, CustomerProjectsQueryParams>({
            query: params => ({
                body: customerProjectsInBCQuery,
                variables: params,
            }),
            // Reads the graph, so admin.createProject's new project must refresh it.
            providesTags: ['project'],
        }),
        project: builder.query<ProjectQueryResult, ProjectQueryParams>({
            query: params => ({
                body: projectQuery,
                variables: params,
            }),
        }),
        projectFilters: builder.query<ProjectFiltersQueryResult, ProjectFiltersQueryParams>({
            query: params => ({
                body: projectFiltersQuery,
                variables: params,
            }),
            providesTags: (_result, _error, params) => [{ type: 'project-sites', id: params.projectId }],
        }),
        enablePdfDownload: builder.mutation<boolean, { projectId: string; eventName: string }>({
            query: params => ({
                body: enablePdfDownloadQuery,
                variables: params,
            }),
            transformResponse: (response: EnablePdfDownloadResponse) => {
                return response.admin?.enablePdfDownload ?? false;
            },
        }),
        projectList: builder.query<ProjectListData, ProjectListInput>({
            query: input => ({
                body: projectListQuery,
                variables: {
                    input: {
                        customerId: input.customerId,
                        pagination: input.pagination,
                        searchText: input.searchText,
                    },
                },
            }),
            transformResponse: (response: ProjectListResponse) => response.projects.projectList,
        }),
        userManagementProjectList: builder.query<ProjectListData, UserManagementProjectListInput>({
            query: input => ({
                body: userManagementProjectListQuery,
                variables: {
                    input: {
                        customerId: input.customerId,
                        pagination: input.pagination,
                        searchText: input.searchText,
                    },
                },
            }),
            transformResponse: (response: UserManagementProjectListResponse) => response.usersQueries.userManagementProjectList,
        }),
        userCountSummary: builder.query<UserCountSummaryQueryResult['userCountSummary'], UserCountSummaryQueryParams>({
            query: ({ projectIds, includeDisabledUser }) => ({
                body: userCountSummaryQuery,
                variables: {
                    projectIds: projectIds.length > 0 ? projectIds : null,
                    includeDisabledUser,
                },
            }),
            transformResponse: (response: UserCountSummaryQueryResult) => response.userCountSummary ?? [],
            providesTags: (_result, _err, arg) => [
                { type: 'UserCountSummary', id: 'LIST' },
                ...arg.projectIds.map(projectId => ({ type: 'UserCountSummary' as const, id: projectId })),
            ],
        }),
        projectSamplingCadence: builder.query<SamplingCadence, ProjectQueryParams>({
            query: params => ({
                body: getProjectSamplingCadenceQuery,
                variables: params,
            }),
            transformResponse: response => response.getProjectSamplingCadence.samplingCadence,
        }),
        projectSites: builder.query<Site[], ProjectQueryParams>({
            query: params => ({
                body: projectSitesQuery,
                variables: params,
            }),
            transformResponse: response => response.getProjectSites.sites,
            providesTags: (_result, _error, params) => [{ type: 'project-sites', id: params.projectId }],
        }),
        projectDefinition: builder.query<ProjectDefinitionQueryResult, ProjectQueryParams>({
            query: params => ({
                body: projectDefinitionQuery,
                variables: params,
            }),
            transformResponse: response => response.projectDefinition,
        }),
        deleteProjectDefinition: builder.mutation<boolean, { projectId: string }>({
            query: params => ({
                body: deleteProjectDefinitionQuery,
                variables: params,
            }),
            transformResponse: (response: DeleteProjectDefinitionResponse) => {
                return response.deleteProjectDefinition;
            },
            // Removing a definition changes the admin projects list the same way saving one does.
            invalidatesTags: ['project'],
        }),
        hasActiveEdnaDataReuploadRequest: builder.query<EdnaDataReuploadRequestStatus | null, { projectId: string }>({
            query: params => ({
                body: hasActiveEdnaDataReuploadRequestQuery,
                variables: params,
            }),
            transformResponse: (response: HasActiveEdnaDataReuploadRequestResult) => {
                return response.admin?.hasActiveEdnaDataReuploadRequest ?? null;
            },
        }),
        getLastEdnaDataReupload: builder.query<EdnaDataReuploadRequestData | null, { projectId: string }>({
            query: params => ({
                body: getLastEdnaDataReuploadQuery,
                variables: params,
            }),
            transformResponse: (response: GetLastEdnaDataReuploadResult) => {
                return response.admin?.getLastEdnaDataReupload ?? null;
            },
        }),
        requestEdnaDataReuploadReview: builder.mutation<boolean, { projectId: string }>({
            query: params => ({
                body: requestEdnaDataReuploadReviewMutation,
                variables: params,
            }),
            transformResponse: (response: RequestEdnaDataReuploadReviewResult) => {
                return response.admin?.requestEdnaDataReuploadReview ?? false;
            },
        }),
        approveEdnaDataReupload: builder.mutation<boolean, { requestId: string }>({
            query: params => ({
                body: approveEdnaDataReuploadMutation,
                variables: params,
            }),
            transformResponse: (response: ApproveEdnaDataReuploadResult) => {
                return response.admin?.approveEdnaDataReupload ?? false;
            },
        }),
        dismissEdnaDataReupload: builder.mutation<boolean, { requestId: string }>({
            query: params => ({
                body: dismissEdnaDataReuploadMutation,
                variables: params,
            }),
            transformResponse: (response: DismissEdnaDataReuploadResult) => {
                return response.admin?.dismissEdnaDataReupload ?? false;
            },
        }),
    }),
});

export const {
    useLazyProjectsQuery,
    useProjectsQuery,
    useProjectQuery,
    useProjectFiltersQuery,
    useCustomerProjectsInBCQuery,
    useEnablePdfDownloadMutation,
    useProjectListQuery,
    useLazyProjectListQuery,
    useUserManagementProjectListQuery,
    useLazyUserManagementProjectListQuery,
    useUserCountSummaryQuery,
    useLazyUserCountSummaryQuery,
    useProjectSamplingCadenceQuery,
    useProjectSitesQuery,
    useLazyProjectSitesQuery,
    useProjectDefinitionQuery,
    useProjectsForAdminQuery,
    useDeleteProjectDefinitionMutation,
    useHasActiveEdnaDataReuploadRequestQuery,
    useGetLastEdnaDataReuploadQuery,
    useRequestEdnaDataReuploadReviewMutation,
    useApproveEdnaDataReuploadMutation,
    useDismissEdnaDataReuploadMutation,
} = projectListGraphSlice;

export const { projects: fetchProjects, project: fetchProject } = projectListGraphSlice.endpoints;
